import type { OidcIntent, OidcProvider } from '@sm/api';
import { isApiClientError } from '@sm/api';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Dialog';
import { Spinner } from '@/components/ui/States';
import { cx } from '@/components/ui/cx';

import { AuthAlert } from './AuthLayout';
import { PROVIDER_LABEL, startOidc, useOidcProviders } from './oidc';

/** Zvanični višebojni „G“ (Google Identity branding). */
export function GoogleLogo({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
      <path fill="none" d="M0 0h48v48H0z" />
    </svg>
  );
}

/** Apple logo (jednobojan, boja iz `currentColor`). */
export function AppleLogo({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 814 1000"
      aria-hidden="true"
      focusable="false"
      fill="currentColor"
    >
      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
    </svg>
  );
}

/** Logo provajdera u malom formatu (lista povezanih naloga). */
export function ProviderMark({ provider }: { provider: OidcProvider }) {
  if (provider === 'google') return <GoogleLogo size={18} />;
  if (provider === 'apple') return <AppleLogo size={17} />;
  return <span className="text-[11px] font-bold tracking-[.04em] uppercase">dev</span>;
}

const GOOGLE_FONT: CSSProperties = {
  fontFamily: "Roboto, 'Helvetica Neue', Arial, system-ui, sans-serif",
};
const APPLE_FONT: CSSProperties = {
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', system-ui, sans-serif",
};

/**
 * Stilovi po zvaničnim smjernicama: Google „Sign in with Google“ (svijetla: #FFFFFF / #747775 / #1F1F1F,
 * tamna: #131314 / #8E918F / #E3E3E3) i Apple HIG (crno dugme u svijetloj temi, bijelo u tamnoj).
 * Ista visina (48px) i radijus kao ostala dugmad prijave.
 */
const providerCls: Record<OidcProvider, string> = {
  google:
    'border border-[#747775] bg-white text-[#1F1F1F] hover:bg-[#F8F9FA] dark:border-[#8E918F] dark:bg-[#131314] dark:text-[#E3E3E3] dark:hover:bg-[#1F1F21]',
  apple:
    'border border-black bg-black text-white hover:bg-[#1d1d1f] dark:border-white dark:bg-white dark:text-black dark:hover:bg-[#f2f2f2]',
  dev: 'border border-dashed border-line2 bg-bg2 text-text2 hover:border-accent hover:text-accent',
};

export function ProviderButton({
  provider,
  label,
  busy,
  disabled,
  onClick,
  unavailable,
}: {
  provider: OidcProvider;
  label: string;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
  unavailable?: boolean;
}) {
  const { t } = useTranslation();
  let logo: ReactNode = null;
  if (provider === 'google') logo = <GoogleLogo />;
  else if (provider === 'apple') logo = <AppleLogo />;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-busy={busy || undefined}
      data-testid={`oidc-${provider}`}
      style={provider === 'google' ? GOOGLE_FONT : provider === 'apple' ? APPLE_FONT : undefined}
      className={cx(
        'relative flex min-h-12 w-full items-center justify-center gap-3 rounded-md px-4 text-[15px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-70',
        providerCls[provider],
      )}
    >
      {busy ? <Spinner /> : logo}
      <span>{label}</span>
      {unavailable ? (
        <span className="rounded bg-current/10 px-2 py-1 text-[10px] font-semibold">
          {t('auth:oidc.inProgress')}
        </span>
      ) : null}
    </button>
  );
}

export function OrDivider() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 text-[13px] font-medium text-muted">
      <span className="h-px flex-1 bg-line" />
      {t('auth:login.or')}
      <span className="h-px flex-1 bg-line" />
    </div>
  );
}

/**
 * Dugmad „Prijavite se putem Google/Apple naloga“; nepodešeni provajderi imaju onemogućen prikaz „U izradi“. `divider` određuje da li „ili“ ide prije ili poslije dugmadi.
 */
export function OidcButtons({
  intent,
  inviteToken,
  returnTo,
  divider = 'before',
}: {
  intent: Exclude<OidcIntent, 'link'>;
  inviteToken?: string;
  returnTo?: string;
  divider?: 'before' | 'after';
}) {
  const { t } = useTranslation();
  const providers = useOidcProviders();
  const [busy, setBusy] = useState<OidcProvider | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const preview = (['google', 'apple'] as const).filter((p) => !providers.includes(p));

  const start = async (p: OidcProvider) => {
    setError(null);
    setBusy(p);
    try {
      await startOidc(p, {
        intent,
        ...(inviteToken ? { inviteToken } : {}),
        ...(returnTo ? { returnTo } : {}),
      });
    } catch (e) {
      setError(isApiClientError(e) ? t(e.i18nKey) : t('errors:network_error'));
      setBusy(null);
    }
  };

  return (
    <>
      {divider === 'before' ? <OrDivider /> : null}
      <div className="flex flex-col gap-3" data-testid="oidc-buttons">
        {providers.map((p) => (
          <ProviderButton
            key={p}
            provider={p}
            label={
              p === 'dev'
                ? t('auth:oidc.devButton')
                : t(`auth:oidc.button.${intent}`, { provider: PROVIDER_LABEL[p] })
            }
            busy={busy === p}
            disabled={busy !== null}
            onClick={() => void start(p)}
          />
        ))}
        {preview.map((p) => (
          <ProviderButton
            key={p}
            provider={p}
            label={t(`auth:oidc.button.${intent}`, { provider: PROVIDER_LABEL[p] })}
            busy={false}
            disabled={false}
            onClick={() => setPreviewOpen(true)}
          />
        ))}
        {error ? <AuthAlert>{error}</AuthAlert> : null}
      </div>
      <Modal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        title={t('auth:oidc.inProgress')}
        testId="oidc-in-progress"
        footer={<Button onClick={() => setPreviewOpen(false)}>{t('common:actions.close')}</Button>}
      >
        <p>{t('panel:platform.login.googleInProgress')}</p>
      </Modal>
      {divider === 'after' ? <OrDivider /> : null}
    </>
  );
}
